import type { ReactElement } from 'react';
import { useCustom } from '@refinedev/core';
import { Row, Col, Card, Statistic, Spin, Alert, Typography } from 'antd';
import {
  CheckCircleOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  ShoppingCartOutlined,
  WarningOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';

import type { DashboardStats, OrderStatus } from '@/types';

// ---------------------------------------------------------------------------
// Status color mapping (matches OrderList)
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<OrderStatus, string> = {
  INIT: '#d9d9d9',
  STARTED: '#faad14',
  PAYMENT_PENDING: '#faad14',
  PAYMENT_COMPLETED: '#1677ff',
  PAYMENT_FAILED: '#ff4d4f',
  GENERATING: '#1677ff',
  GENERATING_PLAN: '#1677ff',
  GENERATING_CHARACTER_SHEET: '#1677ff',
  GENERATING_FINAL: '#1677ff',
  PLAN_REVIEW: '#fa8c16',
  CHARACTER_SHEET_REVIEW: '#fa8c16',
  GENERATION_COMPLETE: '#52c41a',
  GENERATION_FAILED: '#ff4d4f',
  PREVIEW_READY: '#52c41a',
  CANCELLED: '#d9d9d9',
  PRINT_JOB_SUBMITTED: '#13c2c2',
  SHIPPED: '#52c41a',
};

const DEFAULT_BAR_COLOR = '#8884d8';

function getStatusColor(status: string): string {
  return STATUS_COLORS[status as OrderStatus] ?? DEFAULT_BAR_COLOR;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

interface StatusChartDatum {
  status: string;
  count: number;
}

function toStatusChartData(
  ordersByStatus: Record<string, number>
): StatusChartDatum[] {
  return Object.entries(ordersByStatus)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DashboardPage(): ReactElement {
  const { data, isLoading, isError } = useCustom<DashboardStats>({
    url: '/api/admin/dashboard/stats',
    method: 'get',
  });

  const stats = data?.data;
  const statusChartData = stats ? toStatusChartData(stats.ordersByStatus) : [];

  return (
    <div style={{ padding: 0 }}>
      <Typography.Title level={3} style={{ marginBottom: 24 }}>
        Dashboard
      </Typography.Title>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : isError || !stats ? (
        <Alert
          type="error"
          message="Failed to load dashboard stats"
          description="Please check your network connection and try refreshing."
          showIcon
        />
      ) : (
        <>
          {/* Top row: 4 key metric cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Finished Orders"
                  value={stats.finishedOrders}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Paid but Unfinished"
                  value={stats.paidUnfinished}
                  prefix={<ClockCircleOutlined style={{ color: '#1677ff' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Started but Unpaid"
                  value={stats.startedUnpaid}
                  prefix={<WarningOutlined style={{ color: '#faad14' }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Errored"
                  value={stats.errored}
                  prefix={
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                  }
                />
              </Card>
            </Col>
          </Row>

          {/* Second row: totals */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title="Total Orders"
                  value={stats.totalOrders}
                  prefix={<ShoppingCartOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card>
                <Statistic
                  title="Total Revenue"
                  value={formatCurrency(stats.totalRevenue)}
                  prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                />
              </Card>
            </Col>
          </Row>

          {/* Chart row: Orders over time (last 30 days) */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card title="Orders Over Time (Last 30 Days)">
                {stats.ordersOverTime.length === 0 ? (
                  <Typography.Text type="secondary">
                    No data available
                  </Typography.Text>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.ordersOverTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value: string) => {
                          const d = new Date(value);
                          return `${d.getMonth() + 1}/${d.getDate()}`;
                        }}
                      />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip
                        labelFormatter={(label) =>
                          new Date(String(label)).toLocaleDateString()
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="Orders"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
          </Row>

          {/* Bottom row: Orders by status */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={24}>
              <Card title="Orders by Status">
                {statusChartData.length === 0 ? (
                  <Typography.Text type="secondary">
                    No data available
                  </Typography.Text>
                ) : (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={statusChartData} margin={{ bottom: 80 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="status"
                        tick={{ fontSize: 11 }}
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                      />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Bar dataKey="count" name="Orders" radius={[4, 4, 0, 0]}>
                        {statusChartData.map((entry) => (
                          <Cell
                            key={entry.status}
                            fill={getStatusColor(entry.status)}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
